import React from "react";

const toEnglishDigits = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/[٠-٩]/g, (d) => d.charCodeAt(0) - 1632)
    .replace(/[۰-۹]/g, (d) => d.charCodeAt(0) - 1776);
};

const InventoryBulkPreviewModal = ({
  open,
  rows = [],
  fileName = "",
  submitting = false,
  onClose,
  onSubmit,
  onRowsChange,
}) => {
  if (!open) return null;

  const getInventoryIsActive = (row) => {
    if (typeof row.inventory_is_active === "boolean") {
      return row.inventory_is_active;
    }
    if (typeof row.is_active === "boolean") {
      return row.is_active;
    }
    return false;
  };

  const getProductStatus = (row) => {
    if (typeof row.product_is_active === "boolean") {
      return row.product_is_active;
    }
    if (typeof row.base_product_is_active === "boolean") {
      return row.base_product_is_active;
    }
    if (typeof row.is_active === "boolean" && row.product_code) {
      return row.is_active;
    }
    return true;
  };

  const isDefaultRow = (row) => {
    return (
      Number(row.price ?? 0) === 0 &&
      Number(row.stock ?? 0) === 0 &&
      Number(row.discount_percent ?? 0) === 0
    );
  };

  const hasFieldChanged = (row, fieldName) => {
    return row?.[`${fieldName}_changed`] === true;
  };

  const updateRows = (updatedRows) => {
    onRowsChange?.(updatedRows);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = [...rows];
    const row = { ...updated[index] };

    const cleanValue = toEnglishDigits(value);

    if (row[`${field}_original`] === undefined) {
      row[`${field}_original`] = row[field] ?? "";
    }

    row[field] = cleanValue;

    const isChanged =
      String(row[`${field}_original`] ?? "") !== String(cleanValue ?? "");

    row[`${field}_changed`] = isChanged;

    if (isChanged) {
      row.isDirty = true;

      if (isDefaultRow(row) && row.wasDefault === undefined) {
        row.wasDefault = true;
      }

      // اگر قبلا default بوده و حالا مقدار گرفته، به صورت خودکار فعالش کن
      if (!isDefaultRow(row) && row.wasDefault) {
        row.inventory_is_active = true;
      }
    }

    updated[index] = row;
    updateRows(updated);
  };

  const handleCheckboxChange = (index, checked) => {
    const updated = [...rows];
    const row = { ...updated[index] };

    if (row.inventory_is_active_original === undefined) {
      row.inventory_is_active_original = getInventoryIsActive(row);
    }

    row.inventory_is_active = checked;
    row.inventory_is_active_changed =
      row.inventory_is_active_original !== checked;
    row.isDirty = true;

    updated[index] = row;
    updateRows(updated);
  };

  const isCheckboxDisabled = (row) => {
    return isDefaultRow(row) && !row.isDirty;
  };

  const getCheckboxValue = (row) => {
    if (isCheckboxDisabled(row)) return false;
    return getInventoryIsActive(row) === true;
  };

  const getFieldClassName = (row, field) => {
    let className =
      "w-full rounded border border-gray-200 px-1 py-0.5 text-center text-sm focus:outline-none focus:ring-1 focus:ring-blue-500";

    if (hasFieldChanged(row, field)) {
      className += " border-blue-400 bg-blue-50";
    }

    return className;
  };

  const renderNumberInput = (row, index, field) => (
    <input
      type="text"
      inputMode="numeric"
      className={getFieldClassName(row, field)}
      value={row[field] ?? ""}
      onChange={(e) => handleFieldChange(index, field, e.target.value)}
      disabled={submitting}
    />
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 text-sm">
      <div className="flex h-[88vh] w-[96%] flex-col rounded-md bg-white shadow-xl">
        <div className="border-b bg-gray-50 px-5 py-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-gray-800">
                پیش‌نمایش ثبت گروهی موجودی
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {fileName ? `فایل: ${fileName} | ` : ""}
                تعداد ردیف‌ها: {rows.length}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-gray-500 transition hover:text-gray-700 disabled:opacity-50"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-gray-100 text-xs text-gray-600">
              <tr>
                {[
                  "ردیف",
                  "شناسه",
                  "کد کالا",
                  "نام",
                  "برند",
                  "دسته",
                  "وضعیت کالا",
                  "قیمت",
                  "موجودی",
                  "تخفیف (%)",
                  "حداقل سفارش",
                  "حداکثر سفارش",
                  "مضرب سفارش",
                  "وضعیت در انبار",
                ].map((title) => (
                  <th
                    key={title}
                    className="border-b px-2 py-1 text-center font-medium"
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => {
                const checkboxDisabled = isCheckboxDisabled(row);
                const checkboxValue = getCheckboxValue(row);
                const productIsActive = getProductStatus(row);

                return (
                  <tr
                    key={row.id ?? row.product_id ?? row.rowIndex ?? index}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="border-b px-2 py-1 text-center">
                      {row.rowIndex ?? index + 1}
                    </td>

                    <td className="border-b px-2 py-1 text-center">
                      {row.id ?? row.product_id ?? ""}
                    </td>

                    <td className="border-b px-2 py-1 text-center">
                      {row.product_code ?? ""}
                    </td>

                    <td className="border-b px-2 py-1">{row.name ?? ""}</td>

                    <td className="border-b px-2 py-1">{row.brand ?? ""}</td>

                    <td className="border-b px-2 py-1">
                      {row.category_name ?? row.category ?? ""}
                    </td>

                    <td className="border-b px-2 py-1 text-center">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${
                          productIsActive
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {productIsActive ? "فعال" : "غیرفعال"}
                      </span>
                    </td>

                    <td className="border-b px-2 py-1">
                      {renderNumberInput(row, index, "price")}
                    </td>

                    <td className="border-b px-2 py-1">
                      {renderNumberInput(row, index, "stock")}
                    </td>

                    <td className="border-b px-2 py-1">
                      {renderNumberInput(row, index, "discount_percent")}
                    </td>

                    <td className="border-b px-2 py-1">
                      {renderNumberInput(row, index, "min_order")}
                    </td>

                    <td className="border-b px-2 py-1">
                      {renderNumberInput(row, index, "max_order")}
                    </td>

                    <td className="border-b px-2 py-1">
                      {renderNumberInput(row, index, "order_multiple")}
                    </td>

                    <td className="border-b px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        checked={checkboxValue}
                        disabled={checkboxDisabled || submitting}
                        onChange={(e) =>
                          handleCheckboxChange(index, e.target.checked)
                        }
                      />
                      {checkboxDisabled && (
                        <div className="mt-0.5 text-[10px] text-gray-400">
                          غیرفعال
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!rows.length && (
                <tr>
                  <td
                    colSpan={14}
                    className="px-3 py-6 text-center text-sm text-gray-500"
                  >
                    داده‌ای برای نمایش وجود ندارد.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-b-md border-t bg-gray-50 px-5 py-3">
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm transition hover:bg-gray-100 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting || !rows.length}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white transition hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? "در حال ثبت..." : "تایید و ثبت گروهی"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryBulkPreviewModal;
